import { supabase } from '../../lib/supabase'

export type OpenShiftState = 'open' | 'filled' | 'cancelled'

export type TeamOpenShift = {
  id: string
  teamId: string
  date: string
  // Absent for a shift an agent gave away that belongs to no shift template.
  shiftName?: string
  startTime: string
  endTime: string
  assignmentName?: string
  note?: string
  state: OpenShiftState
  filledByName?: string
  // The agent giving the shift away, when it is a giveaway and not a posted slot.
  releasedByName?: string
  // Claims still waiting on a decision.
  pendingClaims: number
}

export type ShiftOption = { id: string; name: string; startTime: string; endTime: string; mealMinutes: number }
export type AssignmentOption = { id: string; name: string }

export type OpenShiftWriteResult = { ok: true } | { ok: false; message: string }

const hourMinute = (value: string) => value.slice(0, 5)

type OpenShiftRow = {
  id: string
  team_id: string
  date: string
  start_time: string
  end_time: string
  note: string | null
  state: OpenShiftState
  released_by_name: string | null
  shift_templates: { name: string } | null
  assignments: { name: string } | null
  employees: { display_name: string } | null
  open_shift_claims: { state: string }[] | null
}

// Every open shift a signed-in supervisor (or administrator) can read, soonest
// first, with how many claims are waiting on a decision.
export async function getTeamOpenShifts(): Promise<TeamOpenShift[]> {
  const { data, error } = await supabase
    .from('open_shifts')
    .select(
      // `employees` is named by its key: the table links to it twice, the agent
      // who filled the slot and the one who gave the shift away.
      'id, team_id, date, start_time, end_time, note, state, released_by_name, shift_templates(name), assignments(name), employees!filled_by_employee_id(display_name), open_shift_claims(state)',
    )
    .order('date', { ascending: true })
  if (error || !data) return []

  return (data as unknown as OpenShiftRow[]).map((row) => ({
    id: row.id,
    teamId: row.team_id,
    date: row.date,
    shiftName: row.shift_templates?.name,
    startTime: hourMinute(row.start_time),
    endTime: hourMinute(row.end_time),
    assignmentName: row.assignments?.name ?? undefined,
    note: row.note ?? undefined,
    state: row.state,
    filledByName: row.employees?.display_name ?? undefined,
    releasedByName: row.released_by_name ?? undefined,
    pendingClaims: (row.open_shift_claims ?? []).filter((claim) => claim.state === 'pending').length,
  }))
}

type ShiftRow = { id: string; name: string; start_time: string; end_time: string; meal_minutes: number; team_id: string | null }

// The shifts a slot can be posted for, a team's own and the office-wide ones.
export async function getShiftOptions(teamId: string): Promise<ShiftOption[]> {
  const { data, error } = await supabase.from('shift_templates').select('id, name, start_time, end_time, meal_minutes, team_id').eq('active', true)
  if (error || !data) return []
  return (data as ShiftRow[])
    .filter((row) => row.team_id === null || row.team_id === teamId)
    .map((row) => ({ id: row.id, name: row.name, startTime: hourMinute(row.start_time), endTime: hourMinute(row.end_time), mealMinutes: row.meal_minutes }))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
}

export async function getAssignmentOptions(teamId: string): Promise<AssignmentOption[]> {
  const { data, error } = await supabase.from('assignments').select('id, name, team_id').eq('active', true)
  if (error || !data) return []
  return (data as { id: string; name: string; team_id: string | null }[])
    .filter((row) => row.team_id === null || row.team_id === teamId)
    .map((row) => ({ id: row.id, name: row.name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export type NewOpenShift = {
  teamId: string
  date: string
  shift: ShiftOption
  assignmentId?: string
  note?: string
}

const POST_REFUSED =
  'That date cannot take an open shift. Its pay period has to be published, and the date cannot be in the past.'

// Posts a slot. Row-level security holds the rules: the caller supervises the
// team, the period holding the date is published, and the date is not past.
export async function postOpenShift(userId: string, shift: NewOpenShift): Promise<OpenShiftWriteResult> {
  const { error } = await supabase.from('open_shifts').insert({
    team_id: shift.teamId,
    date: shift.date,
    shift_template_id: shift.shift.id,
    assignment_id: shift.assignmentId ?? null,
    start_time: shift.shift.startTime,
    end_time: shift.shift.endTime,
    meal_minutes: shift.shift.mealMinutes,
    note: shift.note?.trim() ? shift.note.trim() : null,
    state: 'open',
    created_by: userId,
    updated_by: userId,
  })
  if (!error) return { ok: true }
  return { ok: false, message: error.code === '42501' ? POST_REFUSED : 'The open shift could not be posted. Try again.' }
}

// Cancels a slot that is still open; its pending claims go with it.
export async function cancelOpenShift(userId: string, id: string): Promise<OpenShiftWriteResult> {
  const { error } = await supabase.from('open_shifts').update({ state: 'cancelled', updated_by: userId }).eq('id', id)
  return error ? { ok: false, message: 'The open shift could not be cancelled. Try again.' } : { ok: true }
}
